import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { analyticsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { StatCard, BandBadge, Spinner, FlagBadge } from '../../components/shared/index';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  UsersIcon, FlagIcon, ChartBarIcon, ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

const BAND_COLORS = { 'X': '#16a34a', 'E+': '#2563eb', 'E': '#6b7280', 'E-': '#dc2626' };

export default function Dashboard() {
  const { user } = useAuth();

  const { data: overview, isLoading } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: () => analyticsApi.overview().then(r => r.data),
  });

  const { data: distHistory } = useQuery({
    queryKey: ['analytics-distribution'],
    queryFn: () => analyticsApi.distribution().then(r => r.data),
  });

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <Spinner size="lg" />
    </div>
  );

  const dist = overview?.distribution;
  const distChartData = dist ? [
    { name: 'X',  value: dist.X?.pct  || 0, count: dist.X?.count  || 0 },
    { name: 'E+', value: dist.E?.pct  || 0, count: dist['E+']?.count || 0 },
    { name: 'E',  value: dist.E?.pct  || 0, count: dist.E?.count  || 0 },
    { name: 'E-', value: dist['E-']?.pct || 0, count: dist['E-']?.count || 0 },
  ] : [];

  const completion = overview?.review_completion;
  const completionPct = completion?.total
    ? Math.round((completion.submitted / completion.total) * 100)
    : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Good morning, {user?.name?.split(' ')[0]} 👋</h1>
        <p className="text-sm text-gray-500 mt-1">
          {user?.tenant?.name} · {overview?.latest_cycle?.name ?? 'No active cycle'}
        </p>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Active employees"  value={overview?.headcount ?? '—'}     color="blue"   icon={UsersIcon} />
        <StatCard title="Active PIPs"       value={overview?.active_pips ?? '—'}   color="red"    icon={FlagIcon}
          subtitle={overview?.active_pips > 0 ? 'Needs attention' : 'All clear'} />
        <StatCard title="Review completion" value={`${completionPct}%`}            color="green"  icon={ClipboardDocumentListIcon}
          subtitle={completion ? `${completion.submitted} / ${completion.total} submitted` : 'No active cycle'} />
        <StatCard title="Current cycle"     value={overview?.latest_cycle?.status ?? '—'} color="purple" icon={ChartBarIcon}
          subtitle={overview?.latest_cycle?.name} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Distribution chart */}
        <div className="lg:col-span-2 card p-6">
          <h2 className="section-title">Performance distribution — latest cycle</h2>
          {distChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={distChartData} barSize={52}>
                  <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                  <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v, name, p) => [`${v}% (${p.payload.count} people)`, 'Share']} />
                  <Bar dataKey="value" radius={[6,6,0,0]}>
                    {distChartData.map((entry) => (
                      <Cell key={entry.name} fill={BAND_COLORS[entry.name]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex gap-4 mt-3 flex-wrap">
                {distChartData.map(d => (
                  <div key={d.name} className="flex items-center gap-2">
                    <BandBadge band={d.name} />
                    <span className="text-sm text-gray-600">{d.value}%</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              No scored cycles yet. <Link to="/cycles" className="text-blue-600 ml-1 hover:underline">Create a cycle →</Link>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="card p-6">
          <h2 className="section-title">Quick actions</h2>
          <div className="space-y-2">
            {[
              { label: 'View employees',      to: '/employees',  emoji: '👥' },
              { label: 'Manage cycles',       to: '/cycles',     emoji: '📅' },
              { label: 'Pending reviews',     to: '/reviews',    emoji: '📋' },
              { label: 'Calibration board',   to: '/cycles',     emoji: '🎯' },
              { label: 'Analytics',           to: '/analytics',  emoji: '📊' },
              { label: 'Settings',            to: '/settings',   emoji: '⚙️' },
            ].map(({ label, to, emoji }) => (
              <Link key={to+label}
                to={to}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors group"
              >
                <span className="text-lg">{emoji}</span>
                <span className="group-hover:text-blue-600 transition-colors">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Distribution trend (multi-cycle) */}
      {distHistory?.length > 1 && (
        <div className="card p-6 mt-6">
          <h2 className="section-title">Distribution trend — last {distHistory.length} cycles</h2>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={distHistory.map(c => ({
              cycle: c.cycle,
              X: c.distribution?.X?.pct || 0,
              'E+': c.distribution?.['E+']?.pct || 0,
              E: c.distribution?.E?.pct || 0,
              'E-': c.distribution?.['E-']?.pct || 0,
            }))}>
              <XAxis dataKey="cycle" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => `${v}%`} />
              {['X','E+','E','E-'].map(b => (
                <Bar key={b} dataKey={b} stackId="a" fill={BAND_COLORS[b]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
